import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchVideoProjects, deleteVideoProject, deleteMultipleVideoProjects } from '@/api';
import { DeleteConfirmDialog } from '@/components/DeleteConfirmDialog';

interface VideoProject {
  id: string;
  title: string;
  description: string;
  status: 'draft' | 'scripting' | 'production' | 'review' | 'published';
  content_type: string;
  created_at: string;
  updated_at: string;
  youtube_video_id?: string;
  thumbnail_url?: string;
}

const statusColors = {
  draft: 'bg-gray-100 text-gray-800',
  scripting: 'bg-blue-100 text-blue-800',
  production: 'bg-yellow-100 text-yellow-800',
  review: 'bg-purple-100 text-purple-800',
  published: 'bg-green-100 text-green-800',
};

export default function VideoProjects() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState<VideoProject[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedProjectIds, setSelectedProjectIds] = useState<Set<string>>(new Set());
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const data = await fetchVideoProjects();
      if (data.success && data.data) {
        setProjects(data.data);
      } else if (Array.isArray(data)) {
        setProjects(data);
      } else {
        console.error('Unexpected response format:', data);
        setProjects([]);
      }
    } catch (error) {
      console.error('Failed to fetch projects:', error);
      setProjects([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredProjects = projects.filter(project => {
    const matchesSearch = project.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      project.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = !statusFilter || project.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const createNewProject = async () => {
    const newProject = {
      title: `New Project ${projects.length + 1}`,
      description: 'New YouTube video project',
      content_type: 'general',
      status: 'draft'
    };

    try {
      const response = await fetch('/api/video-projects', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(newProject),
      });
      const data = await response.json();
      if (data.success && data.data?.id) {
        navigate(`/projects/${data.data.id}`);
      } else {
        console.error('Failed to create project:', data);
        await fetchProjects();
      }
    } catch (error) {
      console.error('Failed to create project:', error);
    }
  };

  const toggleProjectSelection = (id: string) => {
    const newSelected = new Set(selectedProjectIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedProjectIds(newSelected);
  };

  const toggleAllSelection = () => {
    if (selectedProjectIds.size === filteredProjects.length) {
      setSelectedProjectIds(new Set());
    } else {
      setSelectedProjectIds(new Set(filteredProjects.map(p => p.id)));
    }
  };

  const handleDeleteClick = (id: string) => {
    setProjectToDelete(id);
    setShowDeleteConfirm(true);
  };

  const handleBulkDeleteClick = () => {
    if (selectedProjectIds.size === 0) return;
    setProjectToDelete(null);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    setIsDeleting(true);
    try {
      if (projectToDelete) {
        // Single delete
        await deleteVideoProject(projectToDelete);
      } else if (selectedProjectIds.size > 0) {
        // Bulk delete
        await deleteMultipleVideoProjects(Array.from(selectedProjectIds));
      }
      setShowDeleteConfirm(false);
      setProjectToDelete(null);
      setSelectedProjectIds(new Set());
      await fetchProjects();
    } catch (error) {
      console.error('Failed to delete project:', error);
      alert('Failed to delete project. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-20 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Video Projects</h1>
        <button
          onClick={createNewProject}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2"
        >
          <span>➕</span>
          <span>New Project</span>
        </button>
      </div>

      {/* Filters */}
      <div className="flex space-x-4 mb-6">
        <div className="flex-1 relative">
          <input
            type="text"
            placeholder="🔍 Search projects..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-4 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          title="Filter by status"
        >
          <option value="">All Status</option>
          <option value="draft">Draft</option>
          <option value="scripting">Scripting</option>
          <option value="production">Production</option>
          <option value="review">Review</option>
          <option value="published">Published</option>
        </select>
      </div>

      {/* Bulk Actions Bar */}
      {selectedProjectIds.size > 0 && (
        <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-center justify-between">
          <span className="text-sm font-medium text-blue-900">
            {selectedProjectIds.size} project{selectedProjectIds.size !== 1 ? 's' : ''} selected
          </span>
          <button
            onClick={handleBulkDeleteClick}
            className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg text-sm font-medium"
          >
            🗑️ Delete Selected
          </button>
        </div>
      )}

      {/* Projects List */}
      {filteredProjects.length === 0 ? (
        <div className="text-center py-12">
          <div className="text-4xl mb-4">🎥</div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No video projects</h3>
          <p className="text-gray-500 mb-4">
            {projects.length === 0
              ? "Get started by creating your first video project"
              : "No projects match your current filters"
            }
          </p>
          {projects.length === 0 && (
            <button
              onClick={createNewProject}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg"
            >
              Create Your First Project
            </button>
          )}
        </div>
      ) : (
        <div className="grid gap-4">
          {/* Select All Row */}
          {filteredProjects.length > 1 && (
            <div className="bg-white rounded-lg border border-gray-200 p-4 flex items-center">
              <input
                type="checkbox"
                title="Select all projects"
                checked={selectedProjectIds.size === filteredProjects.length}
                onChange={toggleAllSelection}
                className="w-5 h-5 text-blue-600 rounded"
              />
              <span className="ml-3 text-sm text-gray-700">
                Select all ({filteredProjects.length})
              </span>
            </div>
          )}

          {filteredProjects.map((project) => (
            <div
              key={project.id}
              className="bg-white rounded-lg border border-gray-200 p-6 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start gap-4">
                {/* Checkbox */}
                <input
                  type="checkbox"
                  title={`Select ${project.title}`}
                  checked={selectedProjectIds.has(project.id)}
                  onChange={() => toggleProjectSelection(project.id)}
                  className="w-5 h-5 text-blue-600 rounded mt-1"
                />

                {/* Content */}
                <div
                  className="flex-1 cursor-pointer"
                  onClick={() => navigate(`/projects/${project.id}`)}
                >
                  <div className="flex items-center space-x-3 mb-2">
                    <h3 className="text-lg font-semibold text-gray-900">
                      {project.title}
                    </h3>
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-medium ${statusColors[project.status]}`}
                    >
                      {project.status}
                    </span>
                  </div>
                  <p className="text-gray-600 mb-3">{project.description}</p>
                  <div className="flex items-center space-x-4 text-sm text-gray-500">
                    <span className="flex items-center space-x-1">
                      <span>📅</span>
                      <span>Created {new Date(project.created_at).toLocaleDateString()}</span>
                    </span>
                    <span className="flex items-center space-x-1">
                      <span>Type: {project.content_type}</span>
                    </span>
                    {project.youtube_video_id && (
                      <span className="flex items-center space-x-1 text-green-600">
                        <span>📊</span>
                        <span>Published</span>
                      </span>
                    )}
                  </div>
                </div>

                {/* Thumbnail */}
                {project.thumbnail_url && (
                  <img
                    src={project.thumbnail_url}
                    alt={project.title}
                    className="w-24 h-14 object-cover rounded-lg"
                  />
                )}

                {/* Delete Button */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteClick(project.id);
                  }}
                  className="px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors ml-2"
                  title="Delete project"
                >
                  🗑️
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmDialog
        isOpen={showDeleteConfirm}
        title={projectToDelete ? 'Delete Project?' : 'Delete Projects?'}
        message={
          projectToDelete
            ? 'Are you sure you want to delete this project? This action cannot be undone.'
            : `Are you sure you want to delete ${selectedProjectIds.size} project${selectedProjectIds.size !== 1 ? 's' : ''}? This action cannot be undone.`
        }
        itemCount={projectToDelete ? 1 : selectedProjectIds.size}
        isLoading={isDeleting}
        onConfirm={confirmDelete}
        onCancel={() => {
          setShowDeleteConfirm(false);
          setProjectToDelete(null);
        }}
      />

      {/* Stats */}
      {projects.length > 0 && (
        <div className="mt-8 grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <div className="text-2xl font-bold text-gray-900">{projects.length}</div>
            <div className="text-sm text-gray-600">Total Projects</div>
          </div>
          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <div className="text-2xl font-bold text-blue-600">
              {projects.filter(p => p.status === 'draft').length}
            </div>
            <div className="text-sm text-gray-600">In Draft</div>
          </div>
          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <div className="text-2xl font-bold text-yellow-600">
              {projects.filter(p => ['scripting', 'production', 'review'].includes(p.status)).length}
            </div>
            <div className="text-sm text-gray-600">In Progress</div>
          </div>
          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <div className="text-2xl font-bold text-green-600">
              {projects.filter(p => p.status === 'published').length}
            </div>
            <div className="text-sm text-gray-600">Published</div>
          </div>
        </div>
      )}
    </div>
  );
}